import React, { useCallback, useState } from 'react';
import { ButtonGroup, makeStyles, TextField } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import AddIcon from 'housecall-ui/dist/IconsAndImages/Icons/Add';
import DeleteIcon from 'housecall-ui/dist/IconsAndImages/Icons/Delete';
import RemoveIcon from 'housecall-ui/dist/IconsAndImages/Icons/Remove';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

type Props = {
  onChange: (value: number) => void;
  initialValue?: number;
  isSmall?: boolean;
  hasDelete?: boolean;
  onDelete?: () => void;
  maxValue?: number;
  allowZero?: boolean;
  value?: number;
  disableDelete?: boolean;
};

type StyleProps = {
  isSmall: boolean;
};

const useStyles = makeStyles((theme: HCPTheme) => ({
  container: {
    marginBottom: 0,
    overflow: 'hidden',
    background: theme.palette.grey[100],
    width: ({ isSmall }: StyleProps) => (isSmall ? '108px' : '140px'),
    height: ({ isSmall }: StyleProps) => (isSmall ? '24px' : '32px'),
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: theme.shadows[1],
    borderRadius: theme.spacing(3),
    '& *': {
      border: 'none !important',
      background: 'none',
      borderRadius: 0,
    },
  },
  input: {
    height: '100%',
    backgroundColor: theme.palette.common.white,
    '& input': {
      textAlign: 'center',
      height: ({ isSmall }: StyleProps) => theme.spacing(isSmall ? 3 : 3),
      padding: 0,
      paddingTop: '1px',
    },
    '& div': {
      backgroundColor: `${theme.palette.common.white} !important`,
      maxHeight: '100%',
      padding: ({ isSmall }: StyleProps) => (isSmall ? 0 : `${theme.spacing(0.5)}px ${theme.spacing(1)}px`),
    },
  },
  icon: ({ isSmall }: StyleProps) =>
    isSmall
      ? {
          padding: 0,
        }
      : {},
  button: ({ isSmall }: StyleProps) => ({
    padding: 0,
    minHeight: theme.spacing(3),
    minWidth: theme.spacing(isSmall ? 3 : 5),
  }),
  buttonDisabled: {
    backgroundColor: theme.palette.grey[300],
  },
}));

export const QuantityInput = ({
  onChange,
  allowZero = false,
  initialValue = 1,
  isSmall = false,
  hasDelete = false,
  disableDelete = false,
  onDelete,
  maxValue,
  value: controlledValue,
}: Props) => {
  const { container, input, icon, button, buttonDisabled } = useStyles({ isSmall });
  const [localValue, setValue] = useState<number>(initialValue);
  const value = controlledValue === undefined ? localValue : controlledValue;
  const setQuantity = useCallback(
    (val: number) => {
      const isAboveMinimum = allowZero ? val >= 0 : val > 0;
      const isNotOverMax = !maxValue || val <= maxValue;
      if (isAboveMinimum && isNotOverMax) {
        setValue(val);
        onChange(val);
      }
    },
    [setValue, onChange, maxValue, allowZero]
  );

  const decreaseHandle = useCallback(() => {
    if (allowZero ? value > 0 : value > 1) {
      setQuantity(value - 1);
    } else if (value === 1 && hasDelete && onDelete) {
      onDelete();
    }
  }, [setQuantity, value, hasDelete, onDelete, allowZero]);

  const increaseHandle = useCallback(() => {
    setQuantity(value + 1);
  }, [setQuantity, value]);

  const handleChange = useCallback(
    ({ target: { value: val } }) => {
      const newValue = Number(val);
      if (!Number.isNaN(newValue)) {
        setQuantity(newValue);
      }
    },
    [setQuantity]
  );

  const iconSize = isSmall ? 'small' : 'medium';

  const minValueReached = value === (allowZero ? 0 : 1) && !hasDelete;
  const disabledDelete = value === 1 && hasDelete && disableDelete;
  const decreaseButtonDisabled = minValueReached || disabledDelete;

  return (
    <ButtonGroup variant="text" className={container}>
      <Button
        disabled={decreaseButtonDisabled}
        data-testid="decrease-quantity-button"
        className={button}
        classes={{ disabled: buttonDisabled }}
        onClick={decreaseHandle}
      >
        {hasDelete && value === 1 ? (
          <DeleteIcon size={iconSize} className={icon} />
        ) : (
          <RemoveIcon size={iconSize} className={icon} />
        )}
      </Button>
      <TextField
        data-testid="quantity-input"
        variant="filled-flat"
        className={input}
        value={value}
        onChange={handleChange}
      />
      <Button
        data-testid="increase-quantity-button"
        className={button}
        onClick={increaseHandle}
        disabled={Boolean(maxValue && value === maxValue)}
      >
        <AddIcon size={iconSize} className={icon} />
      </Button>
    </ButtonGroup>
  );
};
